import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Input, Button, DatePicker, Radio, Tag, Badge, Popover, Modal, Spin, Empty,
  Typography, Space, message
} from 'antd';
import {
  WarningFilled, MessageFilled, DownloadOutlined, SaveOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchHoldTimeTracker, postHoldTimeRatification } from './api';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const MANUFACTURING_STAGES = [
  'Dispensing', 'Granulation', 'Blending', 'Compression',
  'Coating', 'Capsulation', 'Imprinting', 'Packing'
];

const GROUP1_STAGES = ['Dispensing', 'Granulation', 'Blending'];
const DERIVED_STAGES = ['Compression', 'Coating', 'Capsulation', 'Imprinting'];

const STAGE_COLORS = {
  Dispensing:   '#1890ff',
  Blending:     '#722ed1',
  Granulation:  '#13c2c2',
  Compression:  '#fa8c16',
  Coating:      '#52c41a',
  Capsulation:  '#eb2f96',
  Imprinting:   '#2f54eb',
  Packing:      '#faad14'
};

const STAGE_BG = {
  Dispensing:   '#e6f7ff',
  Blending:     '#f9f0ff',
  Granulation:  '#e6fffb',
  Compression:  '#fff7e6',
  Coating:      '#f6ffed',
  Capsulation:  '#fff0f6',
  Imprinting:   '#f0f5ff',
  Packing:      '#fffbe6'
};

const SUB_COLS = ['BATCH NO.', 'START', 'END', 'STATUS', 'HOLD ACT.', 'LIMIT'];
const DISPENSING_SUB_COLS = ['BATCH NO.', 'START', 'END', 'STATUS'];
const getSubCols = (stage) => stage === 'Dispensing' ? DISPENSING_SUB_COLS : SUB_COLS;

const DATE_PRESETS = [
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: 'All', label: 'All', days: 365 },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  return dayjs(iso).format('DD MMM YY HH:mm');
};

const StatusTag = ({ status }) => {
  if (!status) return <span style={{ color: '#bfbfbf' }}>—</span>;
  if (status === 'Done') return <Tag color="green" style={{ margin: 0 }}>✓ Done</Tag>;
  return <Tag color="orange" style={{ margin: 0 }}>⏳ Pending</Tag>;
};

const HoldActCell = ({ stage, onRatify }) => {
  const { hold_actual_days, hold_limit_days, ratification_message, ratified_by } = stage;
  if (hold_actual_days == null) return <span style={{ color: '#bfbfbf' }}>—</span>;

  const exceeded = hold_limit_days != null && hold_actual_days > hold_limit_days;
  const ok = hold_limit_days != null && hold_actual_days <= hold_limit_days;
  let color = '#8c8c8c';
  if (ok) color = '#52c41a';
  if (exceeded) color = '#ff4d4f';

  const hasRatification = !!ratification_message;

  const ratificationContent = (
    <div style={{ maxWidth: 280 }}>
      <div style={{ marginBottom: 4 }}><Text strong>Ratification:</Text></div>
      <div style={{ marginBottom: 4 }}>{ratification_message}</div>
      <div><Text type="secondary">— {ratified_by}</Text></div>
    </div>
  );

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {/* Time + warning icon wrapped together — clickable to open edit ratification */}
      {exceeded ? (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
          onClick={() => onRatify(stage)}
        >
          <span style={{ color, fontWeight: 700, fontSize: 13 }}>{hold_actual_days}d</span>
          <WarningFilled style={{ color: hasRatification ? '#faad14' : '#ff4d4f', fontSize: 13 }} />
        </span>
      ) : (
        <span style={{ color, fontWeight: 700, fontSize: 13 }}>{hold_actual_days}d</span>
      )}
      {/* Message icon — separate, shows ratification popover */}
      {hasRatification && (
        <Popover content={ratificationContent} title={null} trigger="click">
          <MessageFilled style={{ color: '#1890ff', fontSize: 13, cursor: 'pointer' }} />
        </Popover>
      )}
    </span>
  );
};

const LimitCell = ({ value }) => {
  if (value == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
  return <span style={{ fontWeight: 600, fontSize: 13 }}>{value}d</span>;
};

// ─── Ratification Modal ──────────────────────────────────────────────────────

const RatificationModal = ({ visible, stage, batchNumber, onClose, onAdd }) => {
  const [ratMsg, setRatMsg] = useState(stage?.ratification_message || '');
  const [ratBy, setRatBy] = useState(stage?.ratified_by || '');

  const stageName = stage?.stage_name || stage?._ratifyLabel || '';

  // Reset form fields whenever the stage prop changes
  useEffect(() => {
    setRatMsg(stage?.ratification_message || '');
    setRatBy(stage?.ratified_by || '');
  }, [stage]);

  const handleAdd = () => {
    if (!ratMsg.trim() || !ratBy.trim()) {
      message.warning('Please fill ratification message and ratified by');
      return;
    }
    onAdd({
      batch_number: batchNumber,
      stage_name: stageName,
      ratification_message: ratMsg.trim(),
      ratified_by: ratBy.trim(),
    });
  };

  if (!stage) return null;

  return (
    <Modal
      title="Hold Time Ratification"
      open={visible}
      onCancel={onClose}
      onOk={handleAdd}
      okText="Add Ratification"
      width={500}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>BATCH NUMBER</Text>
            <div><Text strong>{batchNumber}</Text></div>
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>STAGE</Text>
            <div><Text strong>{stageName}</Text></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>HOLD ACTUAL</Text>
            <div><Text strong style={{ color: '#ff4d4f' }}>{stage.hold_actual_days}d</Text></div>
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>HOLD LIMIT</Text>
            <div><Text strong>{stage.hold_limit_days != null ? `${stage.hold_limit_days}d` : '—'}</Text></div>
          </div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>RATIFICATION MESSAGE</Text>
          <Input.TextArea
            rows={3}
            value={ratMsg}
            onChange={(e) => setRatMsg(e.target.value)}
            placeholder="Describe reason for delay and approval..."
          />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>RATIFIED BY</Text>
          <Input
            value={ratBy}
            onChange={(e) => setRatBy(e.target.value)}
            placeholder="e.g. John Doe"
          />
        </div>
      </div>
    </Modal>
  );
};

// ─── Normalize API response into flat display rows ──────────────────────────

const normalizeToRows = (parentBatches) => {
  const rows = [];

  parentBatches.forEach((parent, parentIdx) => {
    const { product_code, batch_number: parentBatch, group1, derived_batches } = parent;

    // Build group1 stage map
    const group1Map = {};
    GROUP1_STAGES.forEach(stage => {
      const key = stage.toLowerCase();
      const data = group1?.[key];
      if (data) {
        group1Map[stage] = { ...data, stage_name: stage };
      }
    });

    // If no derived batches, create one row with just group1
    if (!derived_batches || derived_batches.length === 0) {
      rows.push({
        _parentIdx: parentIdx,
        _rowKey: `${parentBatch}_none_0`,
        parentBatch,
        product_code,
        group1Map,
        derivedBatch: null,
        derivedMap: {},
        packing: null,
        overarching: null,
        parentRowSpan: 1,
        derivedRowSpan: 1,
        isFirstParentRow: true,
        isFirstDerivedRow: true,
      });
      return;
    }

    // Calculate total rows for this parent
    let totalParentRows = 0;
    derived_batches.forEach(db => {
      totalParentRows += Math.max((db.packing || []).length, 1);
    });

    let isFirstParent = true;

    derived_batches.forEach((db) => {
      const derivedMap = {};
      DERIVED_STAGES.forEach(stage => {
        const key = stage.toLowerCase();
        const data = db[key];
        if (data) {
          derivedMap[stage] = { ...data, stage_name: stage };
        }
      });

      const packingEntries = db.packing && db.packing.length > 0 ? db.packing : [null];
      const derivedRowCount = packingEntries.length;
      let isFirstDerived = true;

      packingEntries.forEach((pe, peIdx) => {
        const packingStage = pe ? { ...pe, stage_name: 'Packing' } : null;
        const overarching = pe?.overarching || null;

        rows.push({
          _parentIdx: parentIdx,
          _rowKey: `${parentBatch}_${db.batch_number}_${peIdx}`,
          parentBatch,
          product_code,
          group1Map,
          derivedBatch: db.batch_number,
          derivedMap,
          packing: packingStage,
          overarching,
          parentRowSpan: isFirstParent ? totalParentRows : 0,
          derivedRowSpan: isFirstDerived ? derivedRowCount : 0,
          isFirstParentRow: isFirstParent,
          isFirstDerivedRow: isFirstDerived,
        });

        isFirstParent = false;
        isFirstDerived = false;
      });
    });
  });

  return rows;
};

// ─── Legend ────────────────────────────────────────────────────────────────────

const Legend = () => (
  <Space size="large" style={{ fontSize: 13 }}>
    <Space size={4}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#f6ffed', border: '1px solid #b7eb8f' }} /> OK</Space>
    <Space size={4}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#fffbe6', border: '1px solid #ffe58f' }} /> Warning</Space>
    <Space size={4}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#fff2f0', border: '1px solid #ffccc7' }} /> Breached</Space>
    <Space size={4}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#f5f5f5', border: '1px solid #d9d9d9' }} /> Ratified ✓</Space>
    <Text type="secondary" style={{ fontSize: 12 }}>* = ongoing elapsed</Text>
  </Space>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const HoldTimeTracker = () => {
  const [searchMode, setSearchMode] = useState('date');
  const [batchInput, setBatchInput] = useState('');
  const [datePreset, setDatePreset] = useState('1W');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [totalBatches, setTotalBatches] = useState(0);
  const [searched, setSearched] = useState(false);

  // ratification
  const [ratModalVisible, setRatModalVisible] = useState(false);
  const [ratStage, setRatStage] = useState(null);
  const [ratBatch, setRatBatch] = useState('');
  const [pendingRatifications, setPendingRatifications] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [expandedParents, setExpandedParents] = useState(new Set());

  const [pickerOpen, setPickerOpen] = useState(false);

  const lastParams = useRef(null);
  const initialLoad = useRef(false);
  const debounceTimer = useRef(null);
  const pendingDateRange = useRef(null);

  const displayRows = useMemo(() => normalizeToRows(batches), [batches]);

  const parentRowCounts = useMemo(() => {
    const counts = {};
    displayRows.forEach(row => {
      counts[row._parentIdx] = (counts[row._parentIdx] || 0) + 1;
    });
    return counts;
  }, [displayRows]);

  const toggleExpand = useCallback((parentIdx) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentIdx)) next.delete(parentIdx);
      else next.add(parentIdx);
      return next;
    });
  }, []);

  const applyPreset = (key) => {
    setDatePreset(key);
    const preset = DATE_PRESETS.find((p) => p.key === key);
    if (preset) {
      const range = [dayjs().subtract(preset.days, 'day'), dayjs()];
      setDateRange(range);
      // Directly trigger search with the new range
      searchByDate(range);
    }
  };

  const searchByDate = useCallback(async (range) => {
    if (!range[0] || !range[1]) { message.warning('Select date range'); return; }
    const params = {
      from_date: range[0].format('YYYY-MM-DD'),
      to_date: range[1].format('YYYY-MM-DD'),
    };
    lastParams.current = params;
    try {
      setLoading(true);
      setSearched(true);
      const res = await fetchHoldTimeTracker(params);
      if (!res.success) { message.error('API error'); return; }
      if (res.batches) {
        setBatches(res.batches);
        setTotalBatches(res.total_batches || res.batches.length);
      } else if (res.group1 || res.derived_batches) {
        setBatches([{ product_code: res.product_code, batch_number: res.batch_number, group1: res.group1, derived_batches: res.derived_batches }]);
        setTotalBatches(1);
      } else {
        setBatches([]);
        setTotalBatches(0);
      }
    } catch {
      message.error('Failed to fetch tracker data');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchByBatch = useCallback(async (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const params = { batch_number: trimmed };
    lastParams.current = params;
    try {
      setLoading(true);
      setSearched(true);
      const res = await fetchHoldTimeTracker(params);
      if (!res.success) { message.error('API error'); return; }
      if (res.batches) {
        setBatches(res.batches);
        setTotalBatches(res.total_batches || res.batches.length);
      } else if (res.group1 || res.derived_batches) {
        setBatches([{ product_code: res.product_code, batch_number: res.batch_number, group1: res.group1, derived_batches: res.derived_batches }]);
        setTotalBatches(1);
      } else {
        setBatches([]);
        setTotalBatches(0);
      }
    } catch {
      message.error('Failed to fetch tracker data');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBatchInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setBatchInput(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (val.trim()) searchByBatch(val);
    }, 600);
  };

  // Cleanup debounce timer
  useEffect(() => {
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, []);

  // Auto-search on mount with default date range
  useEffect(() => {
    if (!initialLoad.current) {
      initialLoad.current = true;
      searchByDate(dateRange);
    }
  }, [searchByDate, dateRange]);

  const refresh = useCallback(async () => {
    if (!lastParams.current) return;
    try {
      setLoading(true);
      const res = await fetchHoldTimeTracker(lastParams.current);
      if (res.batches) { setBatches(res.batches); setTotalBatches(res.total_batches || res.batches.length); }
      else if (res.group1 || res.derived_batches) { setBatches([{ product_code: res.product_code, batch_number: res.batch_number, group1: res.group1, derived_batches: res.derived_batches }]); setTotalBatches(1); }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const addRatification = (entry) => {
    const key = `${entry.batch_number}|${entry.stage_name}`;
    setPendingRatifications((prev) => ({ ...prev, [key]: entry }));
    setRatModalVisible(false);
    setRatStage(null);
    message.info('Ratification added — click "Save All" to submit');
  };

  const saveAllRatifications = async () => {
    const entries = Object.values(pendingRatifications);
    if (!entries.length) return;
    try {
      setSavingAll(true);
      await Promise.all(entries.map((e) => postHoldTimeRatification(e)));
      message.success(`${entries.length} ratification(s) saved`);
      setPendingRatifications({});
      refresh();
    } catch {
      message.error('Failed to save some ratifications');
    } finally {
      setSavingAll(false);
    }
  };

  const openRatify = (batchNumber, stage) => {
    const key = `${batchNumber}|${stage.stage_name || stage._ratifyLabel || ''}`;
    const pending = pendingRatifications[key];
    if (pending) {
      setRatStage({ ...stage, ratification_message: pending.ratification_message, ratified_by: pending.ratified_by });
    } else {
      setRatStage(stage);
    }
    setRatBatch(batchNumber);
    setRatModalVisible(true);
  };

  const openOverarchingRatify = (batchNumber, oa) => {
    const key = `${batchNumber}|Overarching`;
    const pending = pendingRatifications[key];
    setRatBatch(batchNumber);
    setRatStage({
      stage_name: 'Overarching',
      _ratifyLabel: 'Overarching',
      hold_actual_days: oa.hold_actual_days,
      hold_limit_days: oa.hold_limit_days,
      ratification_message: pending?.ratification_message || oa.ratification_message,
      ratified_by: pending?.ratified_by || oa.ratified_by,
    });
    setRatModalVisible(true);
  };

  const exportTracker = () => {
    if (!batches.length) {
      message.info('No tracker data to export');
      return;
    }

    // Sub-columns per stage
    const stageSubs = (stage) => {
      if (stage === 'Dispensing') return ['Batch No', 'Start', 'End', 'Status'];
      return ['Batch No', 'Start', 'End', 'Status', 'Hold Act.', 'Limit', 'Ratification', 'Ratified By'];
    };

    // Row 1: merged stage group names (empty for identity cols, stage name repeated per sub-col)
    const headerRow1 = [
      '', '', '',
      ...MANUFACTURING_STAGES.flatMap(s => stageSubs(s).map(() => s)),
      'Overarching', 'Overarching', 'Overarching', 'Overarching', 'Overarching',
    ];

    // Row 2: sub-column names
    const headerRow2 = [
      '#', 'Product Code', 'Product Batch No',
      ...MANUFACTURING_STAGES.flatMap(s => stageSubs(s)),
      'Hold Act.', 'Limit', 'Status', 'Ratification', 'Ratified By',
    ];

    const allRows = normalizeToRows(batches);
    const csvRows = [];
    let rowNum = 0;

    allRows.forEach(row => {
      if (row.isFirstParentRow) rowNum++;

      const stageValues = (stageName) => {
        let st;
        if (GROUP1_STAGES.includes(stageName)) st = row.group1Map[stageName];
        else if (DERIVED_STAGES.includes(stageName)) st = row.derivedMap[stageName];
        else if (stageName === 'Packing') st = row.packing;

        if (!st) {
          return stageName === 'Dispensing' ? ['', '', '', ''] : ['', '', '', '', '', '', '', ''];
        }

        const base = [
          st.batch_number || '',
          st.stage_start_time ? dayjs(st.stage_start_time).format('DD MMM YY HH:mm') : '',
          st.stage_end_time ? dayjs(st.stage_end_time).format('DD MMM YY HH:mm') : '',
          st.status || '',
        ];
        if (stageName === 'Dispensing') return base;
        return [
          ...base,
          st.hold_actual_days != null ? `${st.hold_actual_days}d` : '',
          st.hold_limit_days != null ? `${st.hold_limit_days}d` : '',
          st.ratification_message || '',
          st.ratified_by || '',
        ];
      };

      const oa = row.overarching;
      const oaValues = oa ? [
        oa.hold_actual_days != null ? `${oa.hold_actual_days}d` : '',
        oa.hold_limit_days != null ? `${oa.hold_limit_days}d` : '',
        oa.status || '',
        oa.ratification_message || '',
        oa.ratified_by || '',
      ] : ['', '', '', '', ''];

      csvRows.push([
        rowNum,
        row.product_code || '',
        row.parentBatch || '',
        ...MANUFACTURING_STAGES.flatMap(s => stageValues(s)),
        ...oaValues,
      ]);
    });

    const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [headerRow1, headerRow2, ...csvRows].map((r) => r.map(escapeCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hold_time_tracker_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build stages that exist across batches (use MANUFACTURING_STAGES constant)
  const activeStages = MANUFACTURING_STAGES;


  return (
    <div>
      {/* ── Header ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space size="middle" align="center">
          <Title level={4} style={{ margin: 0 }}>Hold Time Tracker</Title>
          {searched && !loading && (
            <Badge
              count={totalBatches}
              style={{ backgroundColor: '#1890ff' }}
              overflowCount={9999}
            />
          )}
        </Space>
        <Legend />
      </div>

      {/* ── Search Controls ───────────────────────────────────────── */}
      <div style={{
        background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8,
        padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20
      }}>
        <Radio.Group value={searchMode} onChange={(e) => setSearchMode(e.target.value)} buttonStyle="solid" size="middle">
          <Radio.Button value="batch">Search by Batch</Radio.Button>
          <Radio.Button value="date">Search by Date Range</Radio.Button>
        </Radio.Group>

        {searchMode === 'batch' ? (
          <Input
            placeholder="e.g. CMOSB26013"
            value={batchInput}
            onChange={handleBatchInputChange}
            style={{ width: 260 }}
            allowClear
          />
        ) : (
          <>
            <Space.Compact>
              {DATE_PRESETS.map((p) => (
                <Button
                  key={p.key}
                  type={datePreset === p.key ? 'primary' : 'default'}
                  size="middle"
                  onClick={() => { applyPreset(p.key); }}
                  style={{ minWidth: 42, fontWeight: datePreset === p.key ? 700 : 400 }}
                >
                  {p.label}
                </Button>
              ))}
            </Space.Compact>
            <RangePicker
              value={dateRange}
              open={pickerOpen}
              onOpenChange={(open) => { if (open) setPickerOpen(true); }}
              onChange={(vals) => { pendingDateRange.current = vals || [null, null]; setDateRange(vals || [null, null]); setDatePreset(null); }}
              format="YYYY-MM-DD"
              style={{ width: 300 }}
              renderExtraFooter={() => (
                <div style={{ textAlign: 'right', padding: '4px 0' }}>
                  <Button type="primary" size="small" onClick={() => { setPickerOpen(false); if (pendingDateRange.current) searchByDate(pendingDateRange.current); }}>OK</Button>
                </div>
              )}
            />
          </>
        )}

        <Space style={{ marginLeft: 'auto' }}>
          {Object.keys(pendingRatifications).length > 0 && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveAllRatifications}
              loading={savingAll}
            >
              Save All Ratifications ({Object.keys(pendingRatifications).length})
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={exportTracker}
            disabled={!batches.length || loading}
          >
            Export Hold Time Tracker
          </Button>
        </Space>
      </div>

      {/* ── Results ───────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" tip="Fetching tracker data..." />
        </div>
      )}

      {!loading && searched && batches.length === 0 && (
        <Empty description="No batches found" style={{ padding: 60 }} />
      )}

      {!loading && batches.length > 0 && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: activeStages.length * 600 + 200, width: '100%' }}>
              {/* ── Grouped header row 1: Stage names ── */}
              <thead>
                <tr>
                  <th style={{ ...thBase, position: 'sticky', left: 0, top: 0, zIndex: 5, background: '#fff', minWidth: 40, borderRight: '1px solid #f0f0f0' }}>#</th>
                  <th style={{ ...thBase, position: 'sticky', left: 40, top: 0, zIndex: 5, background: '#fff', minWidth: 130, borderRight: '1px solid #f0f0f0' }}>PRODUCT</th>
                  <th style={{ ...thBase, position: 'sticky', left: 170, top: 0, zIndex: 5, background: '#fff', minWidth: 130, borderRight: '1px solid #f0f0f0' }}>BATCH NO.</th>
                  {activeStages.map((stage) => (
                    <th
                      key={stage}
                      colSpan={getSubCols(stage).length}
                      style={{
                        ...thBase,
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        textAlign: 'center',
                        fontWeight: 700,
                        color: STAGE_COLORS[stage],
                        backgroundColor: STAGE_BG[stage],
                        borderLeft: `3px solid ${STAGE_COLORS[stage]}`,
                        borderRight: '1px solid #f0f0f0',
                        fontSize: 13,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {stage}
                    </th>
                  ))}
                  {/* Overarching column */}
                  <th
                    style={{
                      ...thBase, textAlign: 'center', fontWeight: 700,
                      position: 'sticky', top: 0, zIndex: 3,
                      color: '#fff', backgroundColor: '#1890ff',
                      borderLeft: '3px solid #096dd9',
                      minWidth: 160,
                    }}
                  >
                    DISP → PACK
                  </th>
                </tr>
                {/* ── Sub-header row: column names per stage ── */}
                <tr>
                  <th style={{ ...thSub, position: 'sticky', left: 0, top: 38, zIndex: 5, background: '#fafafa', borderRight: '1px solid #f0f0f0' }} />
                  <th style={{ ...thSub, position: 'sticky', left: 40, top: 38, zIndex: 5, background: '#fafafa', borderRight: '1px solid #f0f0f0' }} />
                  <th style={{ ...thSub, position: 'sticky', left: 170, top: 38, zIndex: 5, background: '#fafafa', borderRight: '1px solid #f0f0f0' }} />
                  {activeStages.map((stage) => {
                    const cols = getSubCols(stage);
                    return cols.map((col, ci) => (
                      <th
                        key={`${stage}-${col}`}
                        style={{
                          ...thSub,
                          position: 'sticky',
                          top: 38,
                          zIndex: 2,
                          backgroundColor: STAGE_BG[stage],
                          borderLeft: ci === 0 ? `3px solid ${STAGE_COLORS[stage]}` : 'none',
                          borderRight: ci === cols.length - 1 ? '1px solid #f0f0f0' : 'none',
                          minWidth: col === 'BATCH NO.' ? 120 : col === 'START' || col === 'END' ? 130 : col === 'STATUS' ? 80 : 70,
                        }}
                      >
                        {col}
                      </th>
                    ));
                  })}
                  {/* Overarching sub-headers */}
                  <th style={{ ...thSub, textAlign: 'center', position: 'sticky', top: 38, zIndex: 2, background: '#e6f7ff', borderLeft: '3px solid #096dd9' }}>
                    HOLD ACT. / LIMIT
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, idx) => {
                  const isExpanded = expandedParents.has(row._parentIdx);
                  const totalRowsForParent = parentRowCounts[row._parentIdx];
                  const hasMultipleRows = totalRowsForParent > 1;

                  // If collapsed and not the first row of this parent, skip
                  if (!isExpanded && !row.isFirstParentRow) return null;

                  const getStageData = (stageName) => {
                    if (GROUP1_STAGES.includes(stageName)) return row.group1Map[stageName];
                    if (DERIVED_STAGES.includes(stageName)) return row.derivedMap[stageName];
                    if (stageName === 'Packing') return row.packing;
                    return null;
                  };

                  const shouldRenderCell = (stageName) => {
                    if (GROUP1_STAGES.includes(stageName)) return row.isFirstParentRow;
                    if (DERIVED_STAGES.includes(stageName)) return row.isFirstDerivedRow;
                    return true;
                  };

                  const getCellRowSpan = (stageName) => {
                    if (!isExpanded) return 1;
                    if (GROUP1_STAGES.includes(stageName)) return row.parentRowSpan;
                    if (DERIVED_STAGES.includes(stageName)) return row.derivedRowSpan;
                    return 1;
                  };

                  const oa = row.overarching;
                  const oaExceeded = oa && oa.hold_limit_days != null && oa.hold_actual_days > oa.hold_limit_days;
                  const oaOk = oa && oa.hold_limit_days != null && oa.hold_actual_days <= oa.hold_limit_days;
                  const oaBatchNumber = row.packing?.batch_number || row.derivedBatch || row.parentBatch;

                  return (
                    <tr key={row._rowKey} style={{
                      borderBottom: '1px solid #f0f0f0',
                      borderTop: row.isFirstParentRow && idx > 0 ? '2px solid #d9d9d9' : 'none',
                    }}>
                      {/* Row # */}
                      {row.isFirstParentRow && (
                        <td rowSpan={isExpanded ? row.parentRowSpan : 1} style={{ ...tdBase, position: 'sticky', left: 0, background: '#fff', zIndex: 2, borderRight: '1px solid #f0f0f0', fontWeight: 600, color: '#8c8c8c', textAlign: 'center', verticalAlign: 'top' }}>
                          {row._parentIdx + 1}
                        </td>
                      )}
                      {/* Product */}
                      {row.isFirstParentRow && (
                        <td rowSpan={isExpanded ? row.parentRowSpan : 1} style={{ ...tdBase, position: 'sticky', left: 40, background: '#fff', zIndex: 2, borderRight: '1px solid #f0f0f0', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {row.product_code}
                        </td>
                      )}
                      {/* Product Batch No */}
                      {row.isFirstParentRow && (
                        <td rowSpan={isExpanded ? row.parentRowSpan : 1} style={{ ...tdBase, position: 'sticky', left: 170, background: '#fff', zIndex: 2, borderRight: '1px solid #f0f0f0', fontSize: 13, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                          {row.parentBatch}
                        </td>
                      )}
                      {/* Stage cells */}
                      {activeStages.map((stageName) => {
                        if (!shouldRenderCell(stageName)) return null;

                        const st = getStageData(stageName);
                        const isNull = !st || st.status == null;
                        const cols = getSubCols(stageName);
                        const rSpan = getCellRowSpan(stageName);
                        const ratifyBatchNum = st?.batch_number || row.derivedBatch || row.parentBatch;

                        const canExpand = hasMultipleRows && (DERIVED_STAGES.includes(stageName) || stageName === 'Packing');

                        return cols.map((col, ci) => {
                          let content;
                          if (isNull) {
                            if (col === 'STATUS') content = <span style={{ color: '#bfbfbf' }}>N/A</span>;
                            else if (col === 'BATCH NO.' && canExpand) {
                              content = (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <span onClick={() => toggleExpand(row._parentIdx)} style={{ cursor: 'pointer', color: '#1890ff', fontSize: 10, userSelect: 'none', lineHeight: 1 }}>
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  <span style={{ color: '#bfbfbf' }}>—</span>
                                  {!isExpanded && <span style={{ fontSize: 10, color: '#1890ff', fontWeight: 600 }}>+{totalRowsForParent - 1}</span>}
                                </span>
                              );
                            }
                            else content = <span style={{ color: '#bfbfbf' }}>—</span>;
                          } else {
                            switch (col) {
                              case 'BATCH NO.': content = canExpand ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <span onClick={() => toggleExpand(row._parentIdx)} style={{ cursor: 'pointer', color: '#1890ff', fontSize: 10, userSelect: 'none', lineHeight: 1 }}>
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{st.batch_number || '—'}</span>
                                  {!isExpanded && <span style={{ fontSize: 10, color: '#1890ff', fontWeight: 600 }}>+{totalRowsForParent - 1}</span>}
                                </span>
                              ) : <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{st.batch_number || '—'}</span>; break;
                              case 'START': content = <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(st.stage_start_time)}</span>; break;
                              case 'END': content = <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(st.stage_end_time)}</span>; break;
                              case 'STATUS': content = <StatusTag status={st.status} />; break;
                              case 'HOLD ACT.': content = <HoldActCell stage={st} onRatify={(s) => openRatify(ratifyBatchNum, s)} />; break;
                              case 'LIMIT': content = <LimitCell value={st.hold_limit_days} />; break;
                              default: content = '—';
                            }
                          }

                          let cellBg = 'transparent';
                          if (!isNull && col === 'HOLD ACT.' && st.hold_actual_days != null) {
                            if (st.hold_limit_days != null) {
                              if (st.hold_actual_days > st.hold_limit_days) {
                                cellBg = st.ratification_message ? '#f5f5f5' : '#fff2f0';
                              } else if (st.hold_limit_days - st.hold_actual_days <= 7 && st.hold_limit_days - st.hold_actual_days >= 0) {
                                cellBg = '#fffbe6';
                              }
                            }
                          }

                          return (
                            <td
                              key={`${row._rowKey}-${stageName}-${col}`}
                              rowSpan={rSpan > 1 ? rSpan : undefined}
                              style={{
                                ...tdBase,
                                borderLeft: ci === 0 ? `3px solid ${STAGE_COLORS[stageName]}40` : 'none',
                                borderRight: ci === cols.length - 1 ? '1px solid #f0f0f0' : 'none',
                                background: cellBg,
                                verticalAlign: rSpan > 1 ? 'top' : 'middle',
                              }}
                            >
                              {content}
                            </td>
                          );
                        });
                      })}

                      {/* Overarching cell */}
                      <td style={{
                        ...tdBase,
                        textAlign: 'center',
                        borderLeft: '3px solid #096dd940',
                        background: oaExceeded ? (oa?.ratification_message ? '#f5f5f5' : '#fff2f0') : oaOk ? '#f6ffed' : 'transparent',
                      }}>
                        {oa ? (
                          <Space direction="vertical" size={2} align="center">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {oaExceeded ? (
                                <span
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
                                  onClick={() => openOverarchingRatify(oaBatchNumber, oa)}
                                >
                                  <span style={{
                                    color: '#ff4d4f',
                                    fontWeight: 700, fontSize: 14
                                  }}>
                                    {oa.hold_actual_days != null ? `${oa.hold_actual_days}d` : '—'}
                                  </span>
                                  <WarningFilled style={{ color: oa.ratification_message ? '#faad14' : '#ff4d4f', fontSize: 13 }} />
                                </span>
                              ) : (
                                <span style={{
                                  color: oaOk ? '#52c41a' : '#8c8c8c',
                                  fontWeight: 700, fontSize: 14
                                }}>
                                  {oa.hold_actual_days != null ? `${oa.hold_actual_days}d` : '—'}
                                </span>
                              )}
                              {oa.ratification_message && (
                                <Popover
                                  content={
                                    <div style={{ maxWidth: 280 }}>
                                      <div style={{ marginBottom: 4 }}><Text strong>Ratification:</Text></div>
                                      <div style={{ marginBottom: 4 }}>{oa.ratification_message}</div>
                                      <div><Text type="secondary">— {oa.ratified_by}</Text></div>
                                    </div>
                                  }
                                  trigger="click"
                                >
                                  <MessageFilled style={{ color: '#1890ff', fontSize: 13, cursor: 'pointer' }} />
                                </Popover>
                              )}
                            </span>
                            {oa.hold_limit_days != null && (
                              <span style={{ fontSize: 11, color: '#8c8c8c' }}>{oa.hold_limit_days}d limit</span>
                            )}
                            <StatusTag status={oa.status} />
                          </Space>
                        ) : <span style={{ color: '#bfbfbf' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ratification Modal */}
      <RatificationModal
        visible={ratModalVisible}
        stage={ratStage}
        batchNumber={ratBatch}
        onClose={() => { setRatModalVisible(false); setRatStage(null); }}
        onAdd={addRatification}
      />
    </div>
  );
};

// ─── Base cell styles ────────────────────────────────────────────────────────

const thBase = {
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const thSub = {
  padding: '6px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdBase = {
  padding: '10px 10px',
  fontSize: 13,
  verticalAlign: 'middle',
};

export default HoldTimeTracker;
